import { ScrollPosition } from '@databases/layout/scroll-position';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  DEFAULT_DATABASE_VIEW_SETTINGS,
  MIN_CARD_WIDTH,
} from '@databases/types/database/view';
import { getCoverField, getDatabaseFields } from '@databases/utils/database/utils';
import { encodeCssUrl } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import { chunk, range, sum } from 'lodash-es';
import svgToMiniDataURI from 'mini-svg-data-uri';
import React, { FunctionComponent, useMemo } from 'react';
import styled from 'styled-components';

import {
  getSkeletonRowSvg,
  useAnimateItems,
  useRefMapWithSizes,
} from '../../../lazy-render';
import { calculateGridColumns } from '../../../utils';
import { useScope } from '../../Scope';
import EntryCardWithMenu from '../entry-card/EntryCardWithMenu';
import { getEntryCardHeightPrediction } from '../entry-card/height';

import AddEntryCard from './AddEntryCard';

const CONTAINER_PADDING = 5;
const GRID_GAP = 20;

const ADD = 'add';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${GRID_GAP}px;
  padding: ${CONTAINER_PADDING}px 0 ${CONTAINER_PADDING}px ${CONTAINER_PADDING}px;
  box-sizing: border-box;
`;

const Row = styled.div<{ columns: number; cardWidth: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${(props) => props.columns},
    ${(props) => props.cardWidth}px
  );
  gap: ${GRID_GAP}px;

  > * {
    width: ${(props) => props.cardWidth}px;
  }
`;

const CardLayoutWrapper = styled.div`
  display: inline-block;
  padding-bottom: 15px;
`;

export interface CardLayoutProps {
  contentId: string;
  isContentEditable: boolean;
  canEditContent: boolean;
  renderSection: ScrollPosition;
  containerWidth: number;
}

const CardLayout: FunctionComponent<CardLayoutProps> = ({
  contentId,
  isContentEditable,
  canEditContent,
  renderSection,
  containerWidth,
}) => {
  const scope = useScope();
  const { view } = useView();
  const viewSettings = view.settings;
  const [entryIds, fieldCount, hasCover] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const databaseDraftView = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      viewSettings,
      includeDeleted: true,
    });
    return [
      databaseDraftView.entries,
      getDatabaseFields(databaseDraftView).length,
      !!getCoverField(databaseDraft, viewSettings?.layout.coverFieldId),
    ] as const;
  });

  /** The IDs of cards to show. A card can be an entry card or the "add entry" card */
  const cardIds = useMemo(
    () => [...entryIds, ...(canEditContent ? [ADD] : [])],
    [entryIds, canEditContent]
  );

  const { columns, columnWidth: cardWidth } = calculateGridColumns({
    containerWidth,
    containerPadding: CONTAINER_PADDING,
    gridGap: GRID_GAP,
    minColumnWidth:
      MIN_CARD_WIDTH[(viewSettings ?? DEFAULT_DATABASE_VIEW_SETTINGS).layout.cardWidth],
  });

  const rowChunks = useMemo(() => chunk(cardIds, columns), [cardIds, columns]);

  const cardRefMap = useRefMapWithSizes({
    getPositions: ({ sizes }) => {
      const result: Record<string, { left: number; top: number; height: number }> = {};
      let currentY = 0;
      for (const cardIds of rowChunks) {
        let currentX = 0;
        const height = Math.max(
          ...cardIds.map(
            (cardId) =>
              sizes[cardId]?.height ??
              getEntryCardHeightPrediction({ cardWidth, fieldCount, hasCover })
          )
        );
        for (const cardId of cardIds) {
          result[cardId] = {
            left: currentX,
            top: currentY,
            height,
          };
          currentX += cardWidth + GRID_GAP;
        }
        currentY += height + GRID_GAP;
      }
      return result;
    },
  });

  /** The rows to show. Each row contains a number of cards. */
  // Cannot memoize because heights might change
  const rows = rowChunks.map((cardIds) => {
    const heights = cardIds.map((cardId) => cardRefMap.positions[cardId].height);
    return {
      cardIds,
      height: Math.max(...heights),
    };
  });

  const renderCardIds = useAnimateItems({
    itemIds: cardIds,
    itemRefMap: cardRefMap,
    renderSection,
  });

  /** The rows that should actually be rendered. These include visible rows, rows that need to be animated and gap rows. */
  const renderRows = useMemo(() => {
    let gapHeight: number[] = [];
    const result: Array<
      { type: 'gap'; height: number } | { type: 'row'; row: (typeof rows)[number] }
    > = [];
    for (const row of rows) {
      if (row.cardIds.some((cardId) => !!renderCardIds.includes(cardId))) {
        if (gapHeight.length > 0) {
          result.push({
            type: 'gap',
            height: sum(gapHeight) + GRID_GAP * (gapHeight.length - 1),
          });
          gapHeight = [];
        }

        result.push({ type: 'row', row });
      } else {
        gapHeight.push(row.height);
      }
    }
    if (gapHeight.length > 0) {
      result.push({
        type: 'gap',
        height: sum(gapHeight) + GRID_GAP * (gapHeight.length - 1),
      });
    }
    return result;
  }, [rows, renderCardIds]);

  const skeletonSvg = useMemo(
    () =>
      svgToMiniDataURI(
        getSkeletonRowSvg(
          range(0, columns).map(
            (i) => cardWidth + (i > 0 && i < columns - 1 ? GRID_GAP : GRID_GAP / 2)
          )
        )
      ),
    [cardWidth, columns]
  );

  const onAdd = () => {
    setTimeout(() => {
      window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
    }, 200);
  };

  return (
    <CardLayoutWrapper>
      <Container data-testid={`card-layout-${view.settings.layout.cardWidth}`}>
        {renderRows.map((row, i) =>
          row.type === 'gap' ? (
            <div
              key={`gap-${i}`}
              style={{
                height: row.height,
                background: `url(${encodeCssUrl(skeletonSvg)}) repeat-y`,
              }}
            />
          ) : (
            <Row key={`row-${i}`} columns={columns} cardWidth={cardWidth}>
              {row.row.cardIds.map((cardId) =>
                cardId === ADD ? (
                  <AddEntryCard
                    key={cardId}
                    ref={cardRefMap.refs[cardId]}
                    onAdd={onAdd}
                  />
                ) : (
                  <EntryCardWithMenu
                    key={cardId}
                    contentId={contentId}
                    entryId={cardId}
                    viewSettings={viewSettings}
                    isReadonly={!isContentEditable}
                    currentViewId={view.id}
                    ref={{
                      current: {
                        entryCardRef: cardRefMap.refs[cardId],
                      },
                    }}
                  />
                )
              )}
            </Row>
          )
        )}
      </Container>
    </CardLayoutWrapper>
  );
};

export default CardLayout;
